import { COLLISION_CELLS_PER_TILE } from "../document/types.ts";

/**
 * Where a presence prop counts its viewer as inside (ADR-058 amendment, P1.3-T4; ADR-071 decision 5): its
 * area's `[column, rowsUp]` cells, placed from the prop's anchor as the renderer draws its frame, resolved
 * once into a bitmap over the collision cells of the world they cover. A character is inside when any cell of
 * their feet box lies in it, the rule a thing's activation cells follow (ADR-053 amendment 2). A zone is laid
 * on the collision cells as its thing's footprint is (P1.6-T78) while an area stays where its art is, a cell
 * straddling two world cells covering both, so the two answer alike for a frame whose corner lies on a cell
 * corner. The test takes the feet point, the feet box and the
 * collision cell in one unit of the caller's choosing: the game passes sixteenths of a tile, the editor world
 * pixels, and the two agree wherever a pixel lands on a sixteenth. Testing reads at most the few cells a feet
 * box spans, however large the area, and allocates nothing.
 */

/**
 * An area as the bitmap of the world collision cells it covers: the first column and row, the size, and one
 * byte per cell, 1 inside.
 */
export interface PresenceArea {
  readonly x0: number;
  readonly y0: number;
  readonly width: number;
  readonly height: number;
  readonly cells: Uint8Array;
}

/**
 * A feet box and the collision cell's size, in the unit of the feet point: `halfWidth` either side of the
 * feet x and `height` above the feet line.
 */
export interface PresenceFeet {
  readonly halfWidth: number;
  readonly height: number;
  readonly cell: number;
}

/**
 * Resolves an area's cells against where its prop stands. A cell whose edge falls between two world cells,
 * as a prop placed at a pixel inside its tile puts it, covers both.
 *
 * @param anchor The prop's anchor in tiles: its frame's bottom-left sits on the bottom-left of this cell.
 * @param cells The area's `[column, rowsUp]` cells in the frame's own space, already mirrored with the prop.
 * @returns The area.
 */
export function presenceArea(anchor: { readonly x: number; readonly y: number }, cells: readonly (readonly [number, number])[]): PresenceArea {
  const left = anchor.x * COLLISION_CELLS_PER_TILE;
  const bottom = (anchor.y + 1) * COLLISION_CELLS_PER_TILE;
  let x0 = Number.POSITIVE_INFINITY;
  let y0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let y1 = Number.NEGATIVE_INFINITY;
  for (const [column, rowsUp] of cells) {
    x0 = Math.min(x0, Math.floor(left + column));
    x1 = Math.max(x1, Math.ceil(left + column + 1) - 1);
    y0 = Math.min(y0, Math.floor(bottom - rowsUp - 1));
    y1 = Math.max(y1, Math.ceil(bottom - rowsUp) - 1);
  }
  if (cells.length === 0) {
    return { x0: 0, y0: 0, width: 0, height: 0, cells: new Uint8Array(0) };
  }
  const width = x1 - x0 + 1;
  const height = y1 - y0 + 1;
  const bitmap = new Uint8Array(width * height);
  for (const [column, rowsUp] of cells) {
    const cx1 = Math.ceil(left + column + 1) - 1;
    const cy1 = Math.ceil(bottom - rowsUp) - 1;
    for (let y = Math.floor(bottom - rowsUp - 1); y <= cy1; y += 1) {
      for (let x = Math.floor(left + column); x <= cx1; x += 1) {
        bitmap[(y - y0) * width + (x - x0)] = 1;
      }
    }
  }
  return { x0, y0, width, height, cells: bitmap };
}

/**
 * Whether any cell of a feet box lies in an area.
 *
 * @param area The area.
 * @param x The feet x.
 * @param y The feet line.
 * @param feet The feet box and the collision cell, in the unit of `x` and `y`.
 * @returns True when the feet are inside.
 */
export function inPresenceArea(area: PresenceArea, x: number, y: number, feet: PresenceFeet): boolean {
  const left = Math.max(area.x0, Math.floor((x - feet.halfWidth) / feet.cell));
  const right = Math.min(area.x0 + area.width - 1, Math.floor((x + feet.halfWidth - 1) / feet.cell));
  const top = Math.max(area.y0, Math.floor((y - feet.height) / feet.cell));
  const bottom = Math.min(area.y0 + area.height - 1, Math.floor((y - 1) / feet.cell));
  for (let row = top; row <= bottom; row += 1) {
    for (let column = left; column <= right; column += 1) {
      if (area.cells[(row - area.y0) * area.width + (column - area.x0)] === 1) {
        return true;
      }
    }
  }
  return false;
}
